import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  VStack,
  HStack,
  Box,
  Text,
  Progress,
} from '@chakra-ui/react';
import { useAuth } from '../../contexts/AuthContext';

interface VerificationStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const VerificationStatusModal: React.FC<VerificationStatusModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { userProfile } = useAuth();
  const status = userProfile?.kycStatus || 'pending';

  const getStatusInfo = () => {
    switch (status) {
      case 'verified':
        return {
          icon: '',

          title: 'Verified',
          color: 'green',
          description: 'You are confirmed eligible for sweepstakes prizes.',
          progress: 100,
        };
      case 'rejected':
        return {
          icon: '',

          title: 'Verification Failed',
          color: 'red',
          description: 'Your verification was rejected. Please contact support for assistance.',
          progress: 0,
        };
      default:
        return {
          icon: '⏳',
          title: 'Pending Verification',
          color: 'orange',
          description: 'Your account is pending verification. Some features may be limited.',
          progress: 50,
        };
    }
  };

  const statusInfo = getStatusInfo();

  const verificationSteps = [
    {
      id: 'email',
      label: 'Email Verified',
      completed: true,
      description: userProfile?.email || 'Email on file',
    },
    {
      id: 'phone',
      label: 'Phone Number',
      completed: !!userProfile?.phoneNumber,
      description: userProfile?.phoneNumber || 'Not provided',
    },
    {
      id: 'identity',
      label: 'Prize eligibility',
      completed: status === 'verified',
      description: status === 'verified' ? 'Confirmed' : 'Confirm age and state once to redeem prizes',
    },
    {
      id: 'address',
      label: 'Address Verification',
      completed: status === 'verified',
      description: status === 'verified' ? 'Address confirmed' : 'Required for full access',
    },
  ];

  const completedSteps = verificationSteps.filter(s => s.completed).length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.900" />
      <ModalContent bg="black" m={0} borderRadius={0}>
        {/* Header */}
        <HStack 
          justify="space-between" 
          px={4} 
          py={4}
          borderBottom="1px solid"
          borderColor="gray.800"
        >
          <Box w="60px" />
          <Text color="white" fontSize="md" fontWeight="semibold">
            Verification
          </Text>
          <Text
            color="gray.400"
            fontSize="md"
            cursor="pointer"
            onClick={onClose}
            _hover={{ color: 'white' }}
            w="60px"
            textAlign="right"
          >
            Done
          </Text>
        </HStack>

        <ModalBody py={6} px={4} overflowY="auto">
          <VStack spacing={6} align="stretch" maxW="500px" mx="auto">
            {/* Status Card */}
            <Box 
              bg={`${statusInfo.color}.900`}
              p={6} 
              borderRadius="xl"
              textAlign="center"
              borderLeft="4px solid"
              borderColor={`${statusInfo.color}.400`}
            >
              <Text fontSize="4xl" mb={2}>{statusInfo.icon}</Text>
              <Text color={`${statusInfo.color}.200`} fontSize="xl" fontWeight="bold">
                {statusInfo.title}
              </Text>
              <Text color={`${statusInfo.color}.300`} fontSize="sm" mt={2}>
                {statusInfo.description}
              </Text>
            </Box>

            {/* Progress */}
            <Box>
              <HStack justify="space-between" mb={2}>
                <Text color="gray.400" fontSize="sm">
                  Verification Progress
                </Text>
                <Text color="white" fontSize="sm" fontWeight="bold">
                  {completedSteps}/{verificationSteps.length}
                </Text>
              </HStack>
              <Progress
                value={(completedSteps / verificationSteps.length) * 100}
                size="sm"
                borderRadius="full"
                bg="gray.800"
                sx={{
                  '& > div': {
                    bg: `${statusInfo.color}.400`,
                  },
                }}
              />
            </Box>

            {/* Verification Steps */}
            <VStack spacing={3} align="stretch">
              <Text color="gray.400" fontSize="xs" textTransform="uppercase">
                Verification Steps
              </Text>
              
              {verificationSteps.map((step, index) => (
                <HStack
                  key={step.id}
                  bg="#1C1C1E"
                  p={4}
                  borderRadius="xl"
                  spacing={4}
                >
                  <Box
                    w="32px"
                    h="32px"
                    borderRadius="full"
                    bg={step.completed ? 'green.500' : 'gray.700'}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {step.completed ? (
                      <Text color="white" fontWeight="bold"></Text>

                    ) : (
                      <Text color="gray.400" fontWeight="bold">{index + 1}</Text>
                    )}
                  </Box>
                  
                  <VStack align="start" flex={1} spacing={0}>
                    <Text color={step.completed ? 'white' : 'gray.400'} fontWeight="medium">
                      {step.label}
                    </Text>
                    <Text color="gray.500" fontSize="xs">
                      {step.description}
                    </Text>
                  </VStack>

                  {!step.completed && step.id !== 'phone' && (
                    <Text color="orange.400" fontSize="xs" fontWeight="semibold">
                      Required
                    </Text>
                  )}
                </HStack>
              ))}
            </VStack>

            {/* Why Verification is Required */}
            <Box>
              <Text color="gray.400" fontSize="xs" textTransform="uppercase" mb={3}>
                Why Verification is Required
              </Text>
              <VStack spacing={2} align="stretch">
                <HStack bg="#1C1C1E" p={3} borderRadius="lg">
                  <Text fontSize="lg"></Text>

                  <Text color="gray.300" fontSize="sm">
                    Required to redeem PSS Coin prizes
                  </Text>
                </HStack>
                <HStack bg="#1C1C1E" p={3} borderRadius="lg">
                  <Text fontSize="lg"></Text>

                  <Text color="gray.300" fontSize="sm">
                    Age verification (18+ required)
                  </Text>
                </HStack>
                <HStack bg="#1C1C1E" p={3} borderRadius="lg">
                  <Text fontSize="lg"></Text>

                  <Text color="gray.300" fontSize="sm">
                    One account per person
                  </Text>
                </HStack>
                <HStack bg="#1C1C1E" p={3} borderRadius="lg">
                  <Text fontSize="lg"></Text>

                  <Text color="gray.300" fontSize="sm">
                    Fraud prevention & account security
                  </Text>
                </HStack>
              </VStack>
            </Box>

            {status !== 'verified' && (
              <Box 
                bg="#1C1C1E" 
                p={4} 
                borderRadius="xl"
                textAlign="center"
              >
                <Text color="gray.400" fontSize="sm">
                  To complete verification, please contact support with a valid government ID
                </Text>
                <Text color="orange.400" fontSize="sm" fontWeight="semibold" mt={2}>
                  support@pokersweepstars.com
                </Text>
              </Box>
            )}

            {/* Spacer for safe area */}
            <Box h="80px" />
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default VerificationStatusModal;